import { AppUrls } from '@/urls';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { apiSdk } from '@/httpClient';
import FormItem from '@/components/Form/FormItem';
import { LoginResponseModel } from '@/__generated';
import moment from 'moment';
import authService from '@/services/authService';
import { useAppContext } from '@/contexts/AppContext';
import { getUrlQueryValue } from '@/hooks/useQuery';
import { appDisplayDateFormat, UNKNOWN_ERROR_MESSAGE } from '@/config';
import Spin from '@/components/Spin';
import { FormValidation } from '@/helpers/formValidations';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import PasswordInput from '@/components/Form/PasswordInput';

export default function LoginPage() {
  const context = useAppContext();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>('');
  const [errorInfo, setErrorInfo] = useState<any>('');

  const history = useHistory();
  const callback = getUrlQueryValue('callback');
  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm();

  const setAuthToken = (response: LoginResponseModel) => {
    if (response && response.success && response.currentUser) {
      let { currentUser, refreshToken, isPasswordExpired } = response;
      authService.saveLoginInfo({
        token: response.accessToken || '',
        userId: response.currentUser.id || '',
      });
      authService.logUserInfo2Sentry(currentUser.id, currentUser.email, currentUser.username);
      authService.saveRefreshToken(refreshToken || '');
      context.onUpdateContext({
        auth: {
          ...currentUser,
          isPasswordExpired: isPasswordExpired,
        } as any,
        authLoaded: true,
      });
      if (isPasswordExpired) {
        history.push(AppUrls.PasswordExpired);
      } else {
        history.push(callback || '/');
      }
    } else {
      if (response.lockoutEnd) {
        setError(t('UserLockedForLogin', { date: appDisplayDateFormat.default.withTime(response.lockoutEnd) }));
        setErrorInfo('');
      } else if (response.accessFailedCount) {
        setError(t(response.message || UNKNOWN_ERROR_MESSAGE));
        setErrorInfo(t('UserAccountWillLocked', { count: response.accessFailedCount }));
      } else {
        setError(t(response.message || UNKNOWN_ERROR_MESSAGE));
        setErrorInfo('');
      }
    }
  };

  const onSubmit = (values: any) => {
    setLoading(true);
    setError('');
    setErrorInfo('');
    apiSdk.AgentAccountService.login({
      body: {
        password: values.password,
        userName: values.userName,
      },
    })
      .then(response => {
        setAuthToken(response);
      })
      .catch(() => {
        setError(t(UNKNOWN_ERROR_MESSAGE));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section className="layout-pt-lg layout-pb-lg bg-blue-2">
      <Helmet>
        <title>Login</title>
      </Helmet>
      <div className="container">
        <div className="row justify-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <Spin loading={loading}>
              <div className="px-50 py-50 sm:px-20 sm:py-20 bg-white shadow-4 rounded-4">
                <AnimatePresence>
                  {error ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="alert alert-danger">
                      <b> {error}</b>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
                <AnimatePresence>
                  {errorInfo ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="alert alert-warning">
                      {errorInfo}
                    </motion.div>
                  ) : null}
                </AnimatePresence>

                <form className="row y-gap-20" onSubmit={handleSubmit(onSubmit)}>
                  <div className="col-12">
                    <h1 className="text-22 fw-500">{t('loginPage_WelcomeBack')}</h1>
                    <p className="mt-10">
                      {t('loginPage_HaveAccount')}
                      <Link to={AppUrls.Register} className="text-blue-1 ml-5">
                        {t('loginPage_SignUpFree')}
                      </Link>
                    </p>
                  </div>

                  <div className="col-12">
                    <FormItem errors={errors} label={t('base.UserName')} name="userName">
                      <input type="text" {...register('userName', FormValidation.Required)} autoComplete="username" />
                    </FormItem>
                  </div>

                  <div className="col-12">
                    <FormItem errors={errors} label={t('base.Password')} name="password">
                      <PasswordInput {...register('password', FormValidation.Required)} autoComplete="current-password" />
                    </FormItem>
                  </div>

                  <div className="col-12">
                    <Link to={AppUrls.ForgotPassword} className="text-14 fw-500 text-blue-1 underline">
                      {t('loginPage_ForgotPassword')}
                    </Link>
                  </div>

                  <div className="col-12">
                    <button type="submit" className="button py-20 -dark-1 bg-blue-1 text-white w-100">
                      {t('base.SignIn')} <div className="icon-arrow-top-right ml-15" />
                    </button>
                  </div>
                  <div>
                    <span style={{ fontSize: 14, color: '#E91E63', fontWeight: 500 }}>
                      *If this is your first time logging into New Omega, please reset your password for security reasons by clicking on “Forgot your
                      password.”
                    </span>
                  </div>
                </form>
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </section>
  );
}
